// Chat attachment helpers.
//
// Today Teddo only accepts plain-text attachments (JSON, Markdown, plain
// text, and a few other text formats): their contents are read in the
// browser and folded into the outgoing message so the model sees them as
// part of the user's turn. The structure here is intentionally extensible —
// images and richer document types (PDF, etc.) can be added later by
// introducing new `kind`s without reworking the call sites.

// Plain-text formats we read inline. Extension -> fenced-code language hint.
export const TEXT_ATTACHMENT_LANGS = {
  json: 'json',
  md: 'markdown',
  markdown: 'markdown',
  txt: '',
  text: '',
  csv: 'csv',
  tsv: '',
  yaml: 'yaml',
  yml: 'yaml',
  xml: 'xml',
  log: '',
}

// `accept` value for the file picker — limits what the OS dialog offers.
export const TEXT_ACCEPT = Object.keys(TEXT_ATTACHMENT_LANGS)
  .map((ext) => `.${ext}`)
  .join(',')

// Guard rails. The real ceiling is the composed-message length limit, but we
// avoid reading obviously-too-big files into memory, and cap how many files
// can ride along on a single turn.
export const MAX_ATTACHMENTS = 5
export const MAX_ATTACHMENT_BYTES = 256 * 1024 // 256 KB per file

function extensionOf(name) {
  const i = name.lastIndexOf('.')
  return i >= 0 ? name.slice(i + 1).toLowerCase() : ''
}

export function isTextAttachment(name) {
  return Object.prototype.hasOwnProperty.call(TEXT_ATTACHMENT_LANGS, extensionOf(name))
}

// Read a single File as text. Resolves to an attachment object, or rejects
// with an Error whose message is a stable code the caller localizes.
export function readTextAttachment(file) {
  return new Promise((resolve, reject) => {
    if (!isTextAttachment(file.name)) {
      reject(new Error('unsupported-type'))
      return
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      reject(new Error('too-large'))
      return
    }
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('read-failed'))
    reader.onload = () => {
      resolve({
        kind: 'text',
        name: file.name,
        size: file.size,
        lang: TEXT_ATTACHMENT_LANGS[extensionOf(file.name)] || '',
        content: String(reader.result || ''),
      })
    }
    reader.readAsText(file)
  })
}

// Pick a fence that won't collide with backtick runs inside the content.
function fenceFor(content) {
  const longest = (content.match(/`+/g) || []).reduce((n, run) => Math.max(n, run.length), 0)
  if (longest >= 3) return '~~~'
  return '```'
}

// Fold typed text + text attachments into the single string the chat action
// expects. Each file becomes a labelled fenced code block.
export function composeMessage(text, attachments) {
  const trimmed = (text || '').trim()
  if (!attachments || attachments.length === 0) return trimmed
  const blocks = attachments
    .filter((a) => a.kind === 'text')
    .map((a) => {
      const fence = fenceFor(a.content)
      return `Attached file "${a.name}":\n${fence}${a.lang}\n${a.content}\n${fence}`
    })
  return [trimmed, ...blocks].filter(Boolean).join('\n\n')
}
